import React from "react";

import { CloudQueue } from "@mui/icons-material";
import { Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";

import { ServiceConnectionModel } from "../../../api/api";
import { AZURE_REGIONS, CloudProviderDefinition, CLOUD_PROVIDER_TYPES, CloudProviderType, getCloudProviderDefinition } from "./Consts";

interface CloudProviderSelectionProps {
	isLoading: boolean;
	availableProviders: CloudProviderDefinition[];
	selectedProvider: CloudProviderType | "";
	selectedTabIndex: number;
	serviceConnectionId: string;
	filteredConnections: ServiceConnectionModel[];
	region: string;
	resourceGroupName: string;
	applicationDisplayNamePrefix: string;
	onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
	onServiceConnectionChange: (serviceConnectionId: string) => void;
	onRegionChange: (region: string) => void;
	onResourceGroupNameChange: (resourceGroupName: string) => void;
	onApplicationDisplayNamePrefixChange: (applicationDisplayNamePrefix: string) => void;
}

export default function CloudProviderSelection({
	isLoading,
	availableProviders,
	selectedProvider,
	selectedTabIndex,
	serviceConnectionId,
	filteredConnections,
	region,
	resourceGroupName,
	applicationDisplayNamePrefix,
	onTabChange,
	onServiceConnectionChange,
	onRegionChange,
	onResourceGroupNameChange,
	onApplicationDisplayNamePrefixChange,
}: CloudProviderSelectionProps) {
	const selectedProviderDefinition = getCloudProviderDefinition(selectedProvider);
	const cloudTabIconSx = { width: 24, height: 24 };

	return (
		<Box>
			<Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
				<CloudQueue color="primary" />
				Cloud Provider
			</Typography>

			{isLoading ? (
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 2 }}>
					<CircularProgress size={20} sx={{ mr: 1 }} />
					<Typography variant="body2">Loading providers...</Typography>
				</Box>
			) : availableProviders.length > 0 ? (
				<Stack spacing={2}>
					<Tabs value={selectedTabIndex} onChange={onTabChange} variant="fullWidth" sx={{ mb: 2 }}>
						{availableProviders.map((provider) => {
							const ProviderIcon = provider.Icon;
							return <Tab key={provider.type} icon={<ProviderIcon sx={cloudTabIconSx} />} label={provider.tabLabel} sx={{ minHeight: 48 }} />;
						})}
					</Tabs>

					{selectedProvider && (
						<FormControl fullWidth required>
							<InputLabel>{selectedProviderDefinition?.connectionLabel || "Cloud Provider Connection"}</InputLabel>
							<Select
								value={serviceConnectionId}
								onChange={(event) => onServiceConnectionChange(event.target.value)}
								label={selectedProviderDefinition?.connectionLabel || "Cloud Provider Connection"}
							>
								{isLoading ? (
									<MenuItem disabled>
										<CircularProgress size={20} sx={{ mr: 1 }} />
										Loading connections...
									</MenuItem>
								) : filteredConnections.length > 0 ? (
									filteredConnections.map((connection) => (
										<MenuItem key={connection.id} value={connection.id}>
											{connection.name} ({connection.statusName})
										</MenuItem>
									))
								) : (
									<MenuItem disabled>No {selectedProviderDefinition?.emptyConnectionLabel || "cloud provider"} connections found</MenuItem>
								)}
							</Select>
						</FormControl>
					)}

					{selectedProvider === CLOUD_PROVIDER_TYPES.AZURE_SUBSCRIPTION && (
						<>
							<FormControl fullWidth required>
								<InputLabel>Region</InputLabel>
								<Select value={region} onChange={(event) => onRegionChange(event.target.value)} label="Region">
									{AZURE_REGIONS.map((region) => (
										<MenuItem key={region.value} value={region.value}>
											{region.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TextField
								label="Resource Group Name"
								value={resourceGroupName}
								onChange={(event) => onResourceGroupNameChange(event.target.value)}
								fullWidth
								required
								placeholder="e.g., my-app-rg"
								helperText="Specify the Azure Resource Group for this environment"
							/>

							<TextField
								label="App Registration Display Name Prefix"
								value={applicationDisplayNamePrefix}
								onChange={(event) => onApplicationDisplayNamePrefixChange(event.target.value)}
								fullWidth
								required
								placeholder="e.g., myapp-"
								helperText="Prefix for Entra ID App Registration application display name"
							/>
						</>
					)}
				</Stack>
			) : (
				<Box sx={{ p: 3, textAlign: "center", bgcolor: "grey.50", borderRadius: 1 }}>
					<Typography variant="body2" color="text.secondary">
						No cloud provider connections found
					</Typography>
					<Typography variant="caption" color="text.secondary">
						Please configure Azure, AWS, or Google Cloud service connections first.
					</Typography>
				</Box>
			)}
		</Box>
	);
}
